import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Check, Loader2, ShieldAlert } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type InvitePreview = {
  email: string;
  role: "owner" | "operator" | "viewer";
  merchant: { name: string; slug: string } | null;
  status: string;
  expires_at: string;
};

type State =
  | { kind: "loading" }
  | { kind: "needs_auth"; preview: InvitePreview | null }
  | { kind: "ready"; preview: InvitePreview }
  | { kind: "wrong_email"; expected: string; actual: string }
  | { kind: "expired" | "revoked" | "accepted" | "not_found" }
  | { kind: "accepting" }
  | { kind: "accepted_ok"; merchantId: string };

/**
 * /invite/:token — landing page for an invited user.
 *
 * States we need to handle:
 *   1. token is bogus / revoked / expired / already accepted
 *   2. user is not signed in → send them to /auth with a return path
 *   3. user is signed in but their email doesn't match the invite
 *   4. everything looks good → show an "Accept" button that calls the RPC
 *   5. accepted → switch the workspace and redirect to /
 *
 * We fetch a preview of the invite via RLS-open SELECT so the page can
 * describe the invite even when the user hasn't yet logged in — that path
 * is intentionally public, but it only exposes non-sensitive fields (who
 * invited you is already implied by the URL) and the caller's email.
 *
 * Note: public SELECT on merchant_invites is NOT allowed by our RLS.
 * The preview query below will return no rows for anonymous users; for
 * them we render the generic "sign in to continue" flow and only fetch
 * details after they're authenticated. This keeps the token from doubling
 * as an unauth'd information leak.
 */
export default function Invite() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { session, hydrated, user, refreshMemberships, switchMerchant } = useAuth();
  const [state, setState] = useState<State>({ kind: "loading" });

  // Fetch a preview once the user is authed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hydrated) return;
      if (!session) {
        setState({ kind: "needs_auth", preview: null });
        return;
      }
      const { data, error } = await supabase
        .from("merchant_invites")
        .select("email, role, status, expires_at, merchant:merchants!inner(name, slug)")
        .eq("token", token)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setState({ kind: "not_found" });
        return;
      }
      const preview = data as unknown as InvitePreview;

      if (preview.status === "accepted") {
        setState({ kind: "accepted" });
        return;
      }
      if (preview.status === "revoked") {
        setState({ kind: "revoked" });
        return;
      }
      if (new Date(preview.expires_at) < new Date()) {
        setState({ kind: "expired" });
        return;
      }

      const callerEmail = user?.email?.toLowerCase() ?? "";
      if (callerEmail !== preview.email.toLowerCase()) {
        setState({ kind: "wrong_email", expected: preview.email, actual: callerEmail });
        return;
      }
      setState({ kind: "ready", preview });
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrated, session, token, user?.email]);

  async function accept() {
    setState({ kind: "accepting" });
    const { data, error } = await supabase.rpc("accept_merchant_invite", { _token: token });
    if (error) {
      toast.error("Could not accept invite", { description: error.message });
      // Re-run the preview logic so the right error state shows.
      setState({ kind: "loading" });
      return;
    }
    const merchantId = data as string;
    await refreshMemberships();
    // Server RPC only sets current_merchant_id if it was NULL (a conservative
    // default). Clicking "Accept and join" is an explicit intent to land in
    // the new workspace, so we override that conservatism here.
    await switchMerchant(merchantId);
    setState({ kind: "accepted_ok", merchantId });
    setTimeout(() => navigate("/", { replace: true }), 800);
  }

  // --- Render ---------------------------------------------------------------
  if (state.kind === "loading") {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-[13px] text-ink-3">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking invite…
        </div>
      </Shell>
    );
  }

  if (state.kind === "needs_auth") {
    // Bounce through /auth with a return path. After sign-in, the callback
    // will route them to "/" by default — we want them back on /invite/:token
    // instead. React Router's <Navigate state={from}> pattern doesn't survive
    // the Supabase redirect, so we use a URL param the sign-in page reads.
    return (
      <Navigate
        to={`/auth?next=${encodeURIComponent(`/invite/${token}`)}`}
        replace
      />
    );
  }

  if (state.kind === "not_found") {
    return (
      <Shell>
        <InviteCard
          tone="berry"
          icon={<ShieldAlert className="h-5 w-5" />}
          title="Invite not found"
          body="The link you followed is invalid. Ask the person who invited you to send it again."
        />
      </Shell>
    );
  }

  if (state.kind === "expired" || state.kind === "revoked" || state.kind === "accepted") {
    const { title, body } =
      state.kind === "expired"
        ? {
            title: "This invite has expired",
            body: "Invites expire after 14 days. Ask the workspace owner to send a fresh one.",
          }
        : state.kind === "revoked"
        ? {
            title: "This invite was revoked",
            body: "The workspace owner cancelled this invite. Reach out to them if you still need access.",
          }
        : {
            title: "This invite was already accepted",
            body: "You may already be a member — check the workspace switcher in your dashboard.",
          };
    return (
      <Shell>
        <InviteCard tone="amber" icon={<ShieldAlert className="h-5 w-5" />} title={title} body={body} />
      </Shell>
    );
  }

  if (state.kind === "wrong_email") {
    return (
      <Shell>
        <InviteCard
          tone="amber"
          icon={<ShieldAlert className="h-5 w-5" />}
          title="This invite is for a different email"
          body={
            <>
              The invite was sent to <strong>{state.expected}</strong>, but you're signed in as{" "}
              <strong>{state.actual}</strong>. Sign out and in with the right account, or ask for a
              fresh invite to your current email.
            </>
          }
        />
      </Shell>
    );
  }

  if (state.kind === "accepting") {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-[13px] text-ink-3">
          <Loader2 className="h-4 w-4 animate-spin" /> Joining the workspace…
        </div>
      </Shell>
    );
  }

  if (state.kind === "accepted_ok") {
    return (
      <Shell>
        <div className="flex items-center gap-2 text-[13px] text-moss">
          <Check className="h-4 w-4" /> You're in. Taking you to the dashboard…
        </div>
      </Shell>
    );
  }

  if (state.kind !== "ready") return null;
  const { preview } = state;
  return (
    <Shell>
      <div className="w-full max-w-[420px] rounded-xl border border-line bg-canvas p-6 shadow-[0_1px_0_hsl(var(--line))]">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-[0.14em] text-ink-3">
          You've been invited
        </div>
        <h1 className="font-serif text-[28px] leading-tight text-ink">
          Join <span className="italic">{preview.merchant?.name ?? "this workspace"}</span>
        </h1>
        <p className="mt-3 text-[13.5px] text-ink-2">
          You'll join as a <strong>{preview.role}</strong>. You can always be removed later by a
          workspace owner.
        </p>
        <button
          onClick={accept}
          className="mt-5 w-full rounded-md bg-ink px-4 py-2.5 text-[13.5px] text-canvas transition-opacity duration-[120ms] hover:opacity-90"
        >
          Accept and join
        </button>
        <Link
          to="/"
          className="mt-3 block text-center text-[12px] text-ink-3 hover:text-ink"
        >
          Not now
        </Link>
      </div>
    </Shell>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers — keeps the invite route self-contained (no AppLayout)
// ---------------------------------------------------------------------------

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-10">
      {children}
    </div>
  );
}

function InviteCard({
  tone,
  icon,
  title,
  body,
}: {
  tone: "moss" | "amber" | "berry";
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
}) {
  const toneMap = {
    moss: "border-[hsl(var(--moss)/0.3)] bg-[hsl(var(--moss)/0.06)] text-moss",
    amber: "border-[hsl(var(--amber)/0.3)] bg-[hsl(var(--amber)/0.06)] text-amber-rf",
    berry: "border-[hsl(var(--berry)/0.3)] bg-[hsl(var(--berry)/0.06)] text-berry",
  }[tone];
  return (
    <div className="w-full max-w-[420px] rounded-xl border border-line bg-canvas p-6 shadow-[0_1px_0_hsl(var(--line))]">
      <div className={`mb-4 inline-flex h-9 w-9 items-center justify-center rounded-md border ${toneMap}`}>
        {icon}
      </div>
      <h1 className="font-serif text-[24px] leading-tight text-ink">{title}</h1>
      <p className="mt-2 text-[13.5px] text-ink-2">{body}</p>
      <Link
        to="/"
        className="mt-5 inline-flex rounded-md bg-ink px-4 py-2 text-[13px] text-canvas hover:opacity-90"
      >
        Back to Routeflow
      </Link>
    </div>
  );
}
